const { CUSTOM_ICON_CURRENCIES, LANGUAGES } = require('../constants');
const { currencyLabelHTML } = require('../html');

/**
 * Gets formatted currency label based on currency code and language
 * @param {string} code - Currency code
 * @param {string} lang - Language code
 * @returns {string} Formatted currency label
 */
function getCurrencyLabel(code, lang = LANGUAGES.EN) {
  const isCustomIconCurrency = CUSTOM_ICON_CURRENCIES.includes(code);

  if (isCustomIconCurrency) {
    return currencyLabelHTML(code);
  }

  return lang === LANGUAGES.EN ? `${code} ` : ` ${code}`;
}

/**
 * Scrapes currency code from the beginning or end of a display string
 * @param {string} displayString - The display string containing currency and amount
 * @param {string} currencyCode - The currency code to remove
 * @returns {string} The display string with currency code removed
 */
function scrapeCurrencyFromDisplay(displayString, currencyCode) {
  if (!displayString || !currencyCode) return displayString;

  // Remove currency code from the beginning or end of the string
  const regex = new RegExp(`^${currencyCode}|${currencyCode}$`, 'gi');
  return displayString.replace(regex, '').trim();
}

/**
 * Formats amount display data for billing scenarios
 * @param {Object} billing - Billing object containing amount information
 * @param {number} amount - Fallback amount value
 * @param {string} currencyCode - Primary currency code
 * @param {string} currencyCodedDefault - Default currency code fallback
 * @param {string} lang - Language code
 * @returns {Object} Formatted amount display data
 */
function formatAmountDisplay(billing, amount, currencyCode, currencyCodedDefault, lang) {
  if (billing?.amount?.display) {
    const billingCurrency = billing.amount.currency || currencyCode || currencyCodedDefault;
    const isCustomIconCurrency = CUSTOM_ICON_CURRENCIES.includes(billingCurrency);

    if (isCustomIconCurrency) {
      // Use getCurrencyLabel for custom icon currencies and scrub currency from display
      const currencyLabel = getCurrencyLabel(billingCurrency, lang);
      const scrubbedAmount = scrapeCurrencyFromDisplay(billing.amount.display, billingCurrency);

      const amountWithCurrency = lang === LANGUAGES.EN
        ? `${currencyLabel}${scrubbedAmount}`
        : `${scrubbedAmount}${currencyLabel}`;

      // Check if we have sub_total for display amount (used in amount elements when fee > 0)
      let displayAmount = scrubbedAmount;
      if (billing?.sub_total?.display) {
        displayAmount = scrapeCurrencyFromDisplay(billing.sub_total.display, billing.sub_total.currency || billingCurrency);
      }

      return {
        amountWithCurrency,
        displayAmount,
        originalAmountDisplay: billing?.original_amount?.display || null,
        currencyLabel,
        useBillingFormat: true
      };
    } else {
      // Use billing display format directly for non-custom icon currencies
      // For non-custom currencies, use sub_total.display if available for the display amount
      let displayAmount = billing.amount.display;
      if (billing?.sub_total?.display) {
        displayAmount = billing.sub_total.display;
      }

      return {
        amountWithCurrency: billing.amount.display,
        displayAmount,
        originalAmountDisplay: billing?.original_amount?.display || null,
        currencyLabel: '',
        useBillingFormat: true
      };
    }
  }

  const currencyLabel = getCurrencyLabel(currencyCode || currencyCodedDefault, lang);
  const amountWithCurrency = lang === LANGUAGES.EN
    ? `${currencyLabel}${amount}`
    : `${amount}${currencyLabel}`;

  return {
    amountWithCurrency,
    displayAmount: amount,
    originalAmountDisplay: null,
    currencyLabel,
    useBillingFormat: false
  };
}

/**
 * Formats fee display data for billing scenarios
 * @param {Object} billing - Billing object containing fee information
 * @param {number} fee - Fallback fee value
 * @param {string} currencyLabel - Currency label for fallback formatting
 * @param {string} lang - Language code
 * @param {string} currencyCode - Primary currency code
 * @param {string} currencyCodedDefault - Default currency code fallback
 * @returns {Object} Formatted fee display data
 */
function formatFeeDisplay(billing, fee, currencyLabel, lang, currencyCode, currencyCodedDefault) {
  if (billing?.fee?.display) {
    const feeCurrency = billing.fee.currency || currencyCode || currencyCodedDefault;
    const isCustomIconCurrency = CUSTOM_ICON_CURRENCIES.includes(feeCurrency);

    if (isCustomIconCurrency) {
      // Use getCurrencyLabel for custom icon currencies and scrub currency from display
      const feeCustomCurrencyLabel = getCurrencyLabel(feeCurrency, lang);
      const scrubbedFee = scrapeCurrencyFromDisplay(billing.fee.display, feeCurrency);

      const feeCurrencyFormatted = lang === LANGUAGES.EN
        ? `+${feeCustomCurrencyLabel}`
        : `${feeCustomCurrencyLabel}+`;

      return {
        displayFee: scrubbedFee,
        feeCurrency: feeCurrencyFormatted,
        feeLabel: billing.fee.label,
        useBillingFormat: true
      };
    } else {
      // Use billing display format directly for non-custom icon currencies
      return {
        displayFee: billing.fee.display,
        feeCurrency: '',
        feeLabel: billing.fee.label,
        useBillingFormat: true
      };
    }
  }

  const feeCurrency = lang === LANGUAGES.EN
    ? `+${currencyLabel}`
    : `${currencyLabel}+`;

  return {
    displayFee: fee,
    feeCurrency,
    feeLabel: null,
    useBillingFormat: false
  };
}

/** * Safely stringifies a billing object for HTML attributes
 * @param {Object} billing - Billing object to stringify
 * @returns {string} Safely stringified billing object or empty string on failure
 */
function safeBillingStringify(billing) {
  if (!billing) return '';

  try {
    // Create a clean copy without undefined values.
    const cleanBilling = JSON.parse(JSON.stringify(billing));
    const jsonString = JSON.stringify(cleanBilling);

    // Escape HTML attribute characters
    return jsonString
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  } catch (error) {
    console.warn('Failed to stringify billing object:', error);
    return '';
  }
}

/**
 * Safely parses a billing object from HTML attribute string
 * @param {string} billingStr - The HTML-escaped billing string from DOM attribute
 * @returns {Object|null} Parsed billing object or null on failure
 */
function safeBillingParse(billingStr) {
  if (!billingStr) return null;

  try {
    // Decode HTML entities
    const decodedStr = billingStr
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    return JSON.parse(decodedStr);
  } catch (error) {
    console.warn('Failed to parse billing JSON:', error, 'Raw string:', billingStr);
    return null;
  }
}

module.exports = {
  getCurrencyLabel,
  scrapeCurrencyFromDisplay,
  formatAmountDisplay,
  formatFeeDisplay,
  safeBillingStringify,
  safeBillingParse,
};